import { LegalPageLayout } from "@/components/legal/LegalPageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const Confidentialite = () => {
  usePageMeta({
    title: "Politique de confidentialité — B8ild",
    description: "Politique de confidentialité et protection des données personnelles de B8ild.",
    url: "https://b8ild.fr/confidentialite",
  });

  return (
    <LegalPageLayout
      title="Politique de Confidentialité"
      subtitle="Dernière mise à jour : Janvier 2026"
    >
      <h2>1. Introduction</h2>
      <p>
        B8ild accorde une grande importance à la protection de vos données personnelles. Cette Politique
        de Confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos
        informations lorsque vous utilisez notre plateforme.
      </p>
      <p>
        En utilisant B8ild, vous acceptez les pratiques décrites dans cette politique.
      </p>

      <h2>2. Responsable du Traitement</h2>
      <p>Le responsable du traitement des données est :</p>
      <p>
        <strong>B8ild LLC</strong><br />
        Email : <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a>
      </p>

      <h2>3. Données Collectées</h2>
      <p>Nous collectons les catégories de données suivantes :</p>

      <h3>3.1. Données d'identification</h3>
      <ul>
        <li>Nom et prénom</li>
        <li>Adresse email</li>
        <li>Mot de passe (chiffré)</li>
      </ul>

      <h3>3.2. Données professionnelles</h3>
      <ul>
        <li>Nom de l'entreprise</li>
        <li>Secteur d'activité</li>
        <li>Nombre d'employés</li>
      </ul>

      <h3>3.3. Données d'utilisation</h3>
      <ul>
        <li>Adresse IP</li>
        <li>Type de navigateur et appareil</li>
        <li>Pages visitées et actions effectuées</li>
        <li>Date et heure de connexion</li>
      </ul>

      <h3>3.4. Données métier (saisies par l'utilisateur)</h3>
      <ul>
        <li>Informations sur les chantiers (noms, montants, dates)</li>
        <li>Factures et documents importés</li>
        <li>Données des membres d'équipe (noms, taux horaires)</li>
        <li>Données financières des projets</li>
      </ul>

      <h3>3.5. Données de paiement</h3>
      <p>
        Les données de paiement (carte bancaire) sont traitées directement par notre prestataire Stripe.
        B8ild n'a jamais accès à vos numéros de carte et ne les stocke pas.
      </p>

      <h2>4. Finalités du Traitement</h2>
      <p>Vos données sont collectées pour les finalités suivantes :</p>
      <ul>
        <li>Gestion de votre compte et authentification</li>
        <li>Fourniture des services de suivi de rentabilité</li>
        <li>Calculs et analyses financières de vos chantiers</li>
        <li>Facturation et gestion des abonnements</li>
        <li>Envoi de notifications liées à votre compte</li>
        <li>Amélioration de nos services</li>
        <li>Support client</li>
        <li>Respect de nos obligations légales</li>
      </ul>

      <h2>5. Base Légale du Traitement</h2>
      <p>Le traitement de vos données repose sur :</p>
      <ul>
        <li><strong>Exécution du contrat</strong> : fourniture des services auxquels vous avez souscrit</li>
        <li><strong>Consentement</strong> : communications marketing (si vous y avez consenti)</li>
        <li><strong>Intérêt légitime</strong> : amélioration des services, sécurité de la plateforme</li>
        <li><strong>Obligation légale</strong> : conservation des factures, lutte contre la fraude</li>
      </ul>

      <h2>6. Destinataires des Données</h2>
      <p>Vos données peuvent être partagées avec nos sous-traitants techniques :</p>

      <table>
        <thead>
          <tr>
            <th>Prestataire</th>
            <th>Finalité</th>
            <th>Localisation</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Supabase</td>
            <td>Hébergement base de données</td>
            <td>Singapour / UE</td>
          </tr>
          <tr>
            <td>Vercel</td>
            <td>Hébergement site web</td>
            <td>USA</td>
          </tr>
          <tr>
            <td>Stripe</td>
            <td>Paiements sécurisés</td>
            <td>USA</td>
          </tr>
          <tr>
            <td>Anthropic</td>
            <td>Analyse IA (données anonymisées)</td>
            <td>USA</td>
          </tr>
          <tr>
            <td>Resend</td>
            <td>Envoi d'emails transactionnels</td>
            <td>USA</td>
          </tr>
          <tr>
            <td>Microsoft Clarity</td>
            <td>Analyse comportementale (anonymisée)</td>
            <td>USA</td>
          </tr>
        </tbody>
      </table>

      <p>
        Ces prestataires sont contractuellement tenus de protéger vos données et de ne les utiliser
        que pour les finalités définies.
      </p>
      <p>
        <strong>Nous ne vendons jamais vos données à des tiers.</strong>
      </p>

      <h2>7. Transferts Internationaux</h2>
      <p>
        Certains de nos prestataires sont situés hors de l'Union Européenne (États-Unis, Singapour).
        Ces transferts sont encadrés par :
      </p>
      <ul>
        <li>Des clauses contractuelles types approuvées par la Commission Européenne</li>
        <li>Des certifications de conformité (ex: Data Privacy Framework)</li>
      </ul>

      <h2>8. Durée de Conservation</h2>
      <table>
        <thead>
          <tr>
            <th>Type de données</th>
            <th>Durée de conservation</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Données de compte</td>
            <td>Durée de l'abonnement + 3 ans</td>
          </tr>
          <tr>
            <td>Données de chantier</td>
            <td>Durée de l'abonnement + 30 jours</td>
          </tr>
          <tr>
            <td>Données de facturation</td>
            <td>10 ans (obligation légale)</td>
          </tr>
          <tr>
            <td>Logs de connexion</td>
            <td>1 an</td>
          </tr>
          <tr>
            <td>Cookies</td>
            <td>13 mois maximum</td>
          </tr>
        </tbody>
      </table>

      <p>
        Après résiliation, vous disposez de 30 jours pour exporter vos données avant leur suppression définitive.
      </p>

      <h2>9. Vos Droits</h2>
      <p>Conformément au RGPD, vous disposez des droits suivants :</p>
      <ul>
        <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
        <li><strong>Droit de rectification</strong> : corriger vos données inexactes</li>
        <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données</li>
        <li><strong>Droit à la limitation</strong> : limiter le traitement de vos données</li>
        <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
        <li><strong>Droit d'opposition</strong> : vous opposer à certains traitements</li>
      </ul>

      <p>
        Pour exercer ces droits, contactez-nous à :{" "}
        <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a>
      </p>
      <p>Nous répondrons à votre demande dans un délai d'un mois.</p>
      <p>
        Vous pouvez également introduire une réclamation auprès de la CNIL (Commission Nationale de
        l'Informatique et des Libertés) :{" "}
        <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
      </p>

      <h2>10. Sécurité</h2>
      <p>
        Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données :
      </p>
      <ul>
        <li>Chiffrement des données en transit (HTTPS/TLS 1.3)</li>
        <li>Chiffrement des mots de passe (bcrypt)</li>
        <li>Authentification sécurisée avec tokens JWT</li>
        <li>Row Level Security (RLS) sur la base de données</li>
        <li>Contrôle d'accès strict basé sur les rôles</li>
        <li>Sauvegardes automatiques quotidiennes</li>
        <li>Surveillance et alertes de sécurité</li>
        <li>Audits de sécurité réguliers</li>
      </ul>

      <h2>11. Cookies et Traceurs</h2>
      <h3>11.1. Qu'est-ce qu'un cookie ?</h3>
      <p>
        Un cookie est un petit fichier texte stocké sur votre appareil lors de votre visite sur notre site.
      </p>

      <h3>11.2. Cookies utilisés par B8ild</h3>
      <p><strong>Cookies strictement nécessaires (sans consentement) :</strong></p>
      <ul>
        <li>Authentification et session utilisateur</li>
        <li>Sécurité (protection CSRF)</li>
        <li>Préférences de langue</li>
      </ul>

      <p><strong>Cookies analytiques (avec consentement) :</strong></p>
      <ul>
        <li>Microsoft Clarity : analyse du comportement utilisateur (clics, scroll).
            Ces données sont anonymisées et nous aident à améliorer l'interface.</li>
      </ul>

      <h3>11.3. Gestion des cookies</h3>
      <p>
        Lors de votre première visite, un bandeau vous permet d'accepter ou refuser les cookies non essentiels.
      </p>
      <p>
        Vous pouvez également configurer votre navigateur pour bloquer les cookies.
        <strong>Attention</strong> : le blocage des cookies essentiels peut empêcher le fonctionnement de la plateforme.
      </p>

      <h2>12. Protection des Mineurs</h2>
      <p>
        B8ild est destiné à un usage professionnel. Les services ne sont pas destinés aux personnes de moins de 18 ans.
        Nous ne collectons pas sciemment de données concernant des mineurs.
      </p>

      <h2>13. Modifications</h2>
      <p>Nous pouvons modifier cette Politique de Confidentialité à tout moment.</p>
      <p>En cas de modification substantielle, nous vous en informerons par :</p>
      <ul>
        <li>Email à l'adresse associée à votre compte</li>
        <li>Notification sur la plateforme</li>
      </ul>
      <p>La date de dernière mise à jour est indiquée en haut de cette page.</p>

      <h2>14. Contact</h2>
      <p>
        Pour toute question concernant cette Politique de Confidentialité ou l'exercice de vos droits :
      </p>
      <p>
        Email : <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a>
      </p>
      <p>Nous nous engageons à répondre à toute demande dans les meilleurs délais.</p>
    </LegalPageLayout>
  );
};

export default Confidentialite;
